/**
 * «Финансы → Оплаты»: сколько оплатили клиенты по CRM (ИП → город, по способу
 * оплаты) и сколько выручки пришло по выпискам тому же ИП. Наличные и
 * переносы банк не видит, поэтому сверяется безнал CRM с выручкой банка.
 * Клик по ИП — его города (только CRM: банк города не знает). Сверху — разбор
 * расхождения по причинам: сдвиг дат зачисления и остаток по каждому каналу.
 *
 * Спека: `docs/specs/2026-09-30-payment-reconcile-design.md`.
 */
import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { DateRangePicker, withPeriod, отчётныеОкна } from "@/components/DateRangePicker";
import { NoAccess } from "@/components/NoAccess";
import { PNL, useCan } from "@/lib/access";
import { type BankSide, type CrmSide, loadReconcile, type Reconcile } from "@/lib/payments";
import { money } from "@/lib/pnl";

const TH = "whitespace-nowrap border-b border-border px-2.5 py-2 text-right align-bottom";
const CELL = "whitespace-nowrap border-b border-border px-2.5 py-1 text-right tabular-nums";
const NAME = "border-b border-border px-3 py-1 text-left";
const EDGE = "border-l border-border";

/** Расхождение считаем заметным от 1 % безнала CRM. */
function diffTone(diff: string, base: string): string {
  const d = Math.abs(Number(diff));
  if (!d) return "text-fg-muted";
  return d > Math.abs(Number(base)) * 0.01 ? "text-err" : "text-fg-muted";
}

/** Сумма разбора со знаком: плюс — в банке больше, минус — меньше. */
function signed(value: string | undefined): string {
  const n = Number(value ?? 0);
  if (!n) return "—";
  return `${n > 0 ? "+" : "−"}${money(Math.abs(n), true)}`;
}

/** Разбор расхождения: причина × ИП. Сумма столбца — расхождение ИП. */
function Breakdown({ report }: { report: Reconcile }) {
  const rows = report.rows.filter((r) => Number(r.diff) !== 0 || Object.keys(r.breakdown).length > 0);
  if (report.lines.length === 0) return null;
  return (
    <section data-annot="разбор расхождения" className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
            <th className={`${NAME} align-bottom`}>
              Разбор расхождения: банк − безнал CRM
              <div className="font-normal">плюс — в выписках больше, чем оплачено в CRM; минус — меньше</div>
            </th>
            {rows.map((r) => (
              <th key={r.entrepreneur_id ?? "unknown"} className={TH}>
                {r.name}
              </th>
            ))}
            <th className={`${TH} ${EDGE}`}>итого</th>
          </tr>
        </thead>
        <tbody>
          {report.lines.map((line) => (
            <tr key={line.code}>
              <td className={NAME}>
                <div>{line.name}</div>
                <div className="max-w-[640px] text-[11px] text-fg-muted">{line.hint}</div>
              </td>
              {rows.map((r) => (
                <td key={r.entrepreneur_id ?? "unknown"} className={`${CELL} align-top`}>
                  {signed(r.breakdown[line.code])}
                </td>
              ))}
              <td className={`${CELL} ${EDGE} align-top font-medium`}>{signed(report.total.breakdown[line.code])}</td>
            </tr>
          ))}
          <tr className="bg-bg-subtle font-medium">
            <td className={NAME}>Расхождение</td>
            {rows.map((r) => (
              <td key={r.entrepreneur_id ?? "unknown"} className={`${CELL} ${diffTone(r.diff, r.crm_cashless)}`}>
                {signed(r.diff)}
              </td>
            ))}
            <td className={`${CELL} ${EDGE} ${diffTone(report.total.diff, report.total.crm_cashless)}`}>
              {signed(report.total.diff)}
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}

export function PaymentsPage() {
  return useCan(PNL) ? <PaymentsScreen /> : <NoAccess what="оплатам" />;
}

function PaymentsScreen() {
  const [params, setParams] = useSearchParams();
  const [report, setReport] = useState<Reconcile | null>(null);
  const [failure, setFailure] = useState("");
  const [open, setOpen] = useState<Set<number | null>>(new Set());

  // Без периода — прошлый месяц: сверяют закрытый месяц, а не текущий.
  const last = отчётныеОкна()[1];
  const from = params.get("date_from") ?? last?.from ?? "";
  const to = params.get("date_to") ?? last?.to ?? "";

  useEffect(() => {
    if (!from || !to) return;
    setFailure("");
    setReport(null);
    loadReconcile({ date_from: from, date_to: to })
      .then(setReport)
      .catch((error: Error) => setFailure(error.message));
  }, [from, to]);

  const toggle = (id: number | null) => {
    const next = new Set(open);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpen(next);
  };

  const channels = report?.channels ?? [];
  const articles = report?.bank_articles ?? [];

  const crmCells = (side: CrmSide, strong = false) => (
    <>
      {channels.map((c, i) => (
        <td key={c.code} className={`${CELL} ${i === 0 ? EDGE : ""} ${c.cashless ? "" : "text-fg-muted"}`}>
          {money(side.crm[c.code], true)}
        </td>
      ))}
      <td className={`${CELL} ${EDGE} font-medium`}>{money(side.crm_total, true)}</td>
      <td className={`${CELL} ${strong ? "font-medium" : ""}`}>{money(side.crm_cashless, true)}</td>
    </>
  );
  const bankCells = (side: CrmSide & BankSide) => (
    <>
      {articles.map((a, i) => (
        <td key={a.code} className={`${CELL} ${i === 0 ? EDGE : ""} ${a.in_total ? "" : "text-fg-muted"}`}>
          {money(side.bank[a.code], true)}
        </td>
      ))}
      <td className={`${CELL} ${articles.length ? "" : EDGE} font-medium`}>{money(side.bank_total, true)}</td>
      <td className={`${CELL} ${EDGE} font-medium ${diffTone(side.diff, side.crm_cashless)}`}>
        {Number(side.diff) > 0 ? "+" : ""}
        {money(side.diff, true)}
      </td>
    </>
  );

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 [&>*]:flex-none">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[12.5px] text-fg-muted">
        <DateRangePicker
          label="период"
          from={from}
          to={to}
          onChange={(f, t) => setParams(withPeriod(params, f, t))}
        />
        <span>
          CRM — оплаченные оплаты по дню подтверждения; город оплаты определяет ИП. Банк — выручка по выпискам
          этого ИП по дате зачисления, как в P&L (эквайринг — с удержанной комиссией). Расхождение = банк −
          безнал CRM; из чего оно состоит — в разборе.
        </span>
      </div>
      {failure ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          {failure}
        </div>
      ) : null}
      {!from || !to ? (
        <div className="text-[12.5px] text-fg-muted">Выберите период с началом и концом.</div>
      ) : report === null ? (
        failure ? null : <div className="text-[12.5px] text-fg-muted">…</div>
      ) : report.rows.length === 0 ? (
        <div className="text-[12.5px] text-fg-muted">За период нет ни оплат в CRM, ни выручки в выписках.</div>
      ) : (
        <>
        <Breakdown report={report} />
        <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
          <table className="w-full border-collapse text-[12px]">
            <thead>
              <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
                <th className={`${NAME} align-bottom`} rowSpan={2}>
                  ИП / город
                </th>
                <th className={`${TH} align-bottom`} rowSpan={2}>
                  оплат
                </th>
                <th colSpan={channels.length + 2} className={`${TH} ${EDGE} text-center text-fg`}>
                  CRM: оплаты клиентов
                </th>
                <th colSpan={articles.length + 1} className={`${TH} ${EDGE} text-center text-fg`}>
                  Банк: выручка по выпискам
                </th>
                <th className={`${TH} ${EDGE}`} rowSpan={2} title="Выручка банка минус безнал CRM">
                  расхождение
                </th>
              </tr>
              <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
                {channels.map((c, i) => (
                  <th key={c.code} className={`${TH} ${i === 0 ? EDGE : ""}`}>
                    {c.name}
                  </th>
                ))}
                <th className={`${TH} ${EDGE}`}>всего</th>
                <th className={TH} title="Без наличных и переносов — то, что должно прийти в банк">
                  безнал
                </th>
                {articles.map((a, i) => (
                  <th
                    key={a.code}
                    className={`${TH} ${i === 0 ? EDGE : ""}`}
                    title={a.in_total ? undefined : "Справочно: в итог банка не входит"}
                  >
                    {a.name}
                  </th>
                ))}
                <th className={`${TH} ${articles.length ? "" : EDGE}`}>итого</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((row) => {
                const opened = open.has(row.entrepreneur_id);
                return (
                  <Fragment key={row.entrepreneur_id ?? "unknown"}>
                    <tr
                      className="cursor-pointer hover:bg-bg-hover"
                      onClick={() => toggle(row.entrepreneur_id)}
                      title="Показать города"
                    >
                      <td className={`${NAME} whitespace-nowrap font-medium`}>
                        <span className="inline-flex items-center gap-1">
                          {opened ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                          {row.name}
                        </span>
                      </td>
                      <td className={CELL}>{row.count || "—"}</td>
                      {crmCells(row, true)}
                      {bankCells(row)}
                    </tr>
                    {opened
                      ? row.locations.map((place) => (
                          <tr key={place.id ?? "none"} className="text-fg-muted">
                            <td className={`${NAME} whitespace-nowrap pl-8`}>{place.name}</td>
                            <td className={CELL}>{place.count}</td>
                            {crmCells(place)}
                            <td className={`${CELL} ${EDGE}`} colSpan={articles.length + 1} />
                            <td className={`${CELL} ${EDGE}`} />
                          </tr>
                        ))
                      : null}
                  </Fragment>
                );
              })}
              <tr className="bg-bg-subtle font-medium">
                <td className={NAME}>Итого</td>
                <td className={CELL}>{report.total.count}</td>
                {crmCells(report.total, true)}
                {bankCells(report.total)}
              </tr>
            </tbody>
          </table>
        </div>
        </>
      )}
    </main>
  );
}
